import { type ReactNode, useState } from 'react';
import { ApiError } from '../lib/api';
import { fmtDate, fmtNum, fmtRelative, initials } from '../lib/format';

// ─── Icons (inline, stroke-based; decorative unless labeled) ────────────────────────────────────────
const P = {
  catalog: 'M4 5h16M4 12h16M4 19h10',
  candidates: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  review: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  ops: 'M22 12h-4l-3 9L9 3l-3 9H2',
  search: 'M21 21l-4.3-4.3M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14z',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  sun: 'M12 3v2M12 19v2M5 5l1.5 1.5M17.5 17.5L19 19M3 12h2M19 12h2M5 19l1.5-1.5M17.5 6.5L19 5M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
  moon: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z',
  globe:
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16v-4M12 8h.01',
  alert:
    'M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  ext: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3',
  arrow: 'M5 12h14M13 5l7 7-7 7',
  up: 'M12 19V5M5 12l7-7 7 7',
  down: 'M12 5v14M19 12l-7 7-7-7',
} as const;
export type IconName = keyof typeof P;
export function Icon({ name, label }: { name: IconName; label?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <path d={P[name]} />
    </svg>
  );
}

// ─── Text helpers ───────────────────────────────────────────────────────────────────────────────────
/** Technical value (ID, domain, hash, URL): always LTR, isolated from surrounding RTL text. */
export const Ltr = ({ children, mono }: { children: ReactNode; mono?: boolean }) => (
  <bdi dir="ltr" className={`ltr${mono ? ' mono' : ''}`}>
    {children}
  </bdi>
);

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="copy"
      title={done ? '✓' : 'Copy'}
      aria-label={`$Copy ${label ?? ''}`.trim()}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        });
      }}
    >
      <Icon name="copy" />
    </button>
  );
}

export function Time({ iso, relative }: { iso: string | null | undefined; relative?: boolean }) {
  if (!iso) return <span className="faint">—</span>;
  return (
    <time dateTime={iso} title={iso} className="nowrap">
      {relative ? fmtRelative(iso, Date.now()) : fmtDate(iso)}
    </time>
  );
}

// ─── Status badges (every tone maps a real persisted V3 value; the raw value is the title) ────────
type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'accent' | 'neutral';
const TONES: Record<string, Record<string, Tone>> = {
  pipeline: {
    PUBLISHED: 'ok',
    APPROVED: 'accent',
    QUARANTINED: 'warn',
    REJECTED: 'bad',
    FAILED: 'bad',
    NOT_FOUND: 'neutral',
  },
  revision: {
    PUBLISHED: 'ok',
    APPROVED: 'accent',
    SUPERSEDED: 'neutral',
    PENDING_REVIEW: 'warn',
    REJECTED: 'bad',
  },
  candidate: {
    published: 'ok',
    awaiting_review: 'warn',
    needs_fetch: 'info',
    ready_offline: 'accent',
    invalid: 'bad',
    duplicate: 'neutral',
    pending: 'neutral',
  },
  run: { succeeded: 'ok', failed: 'bad', running: 'info', skipped: 'neutral' },
  review: { OPEN: 'warn', APPROVED: 'ok', REJECTED: 'neutral', SUPERSEDED: 'neutral' },
};
export const humanize = (v: string) =>
  v
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

export function StatusBadge({
  kind,
  value,
  tone,
}: {
  kind: keyof typeof TONES;
  value: string;
  tone?: Tone;
}) {
  const tn = tone ?? TONES[kind]?.[value] ?? 'info';
  return (
    <span
      className={`badge ${tn === 'neutral' ? '' : tn}`}
      title={`${kind}: ${value}`}
      data-state={value}
    >
      <span className="dot" />
      {humanize(value)}
    </span>
  );
}

export const Badge = ({
  tone,
  children,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  title?: string;
}) => (
  <span className={`badge ${tone && tone !== 'neutral' ? tone : ''}`} title={title}>
    {children}
  </span>
);

// ─── Logos ──────────────────────────────────────────────────────────────────────────────────────────
/** A public CDN logo; if the object is not reachable the thumb says so instead of faking one. */
export function LogoThumb({
  url,
  name,
  size,
}: {
  url: string | null | undefined;
  name: string;
  size?: 'lg';
}) {
  const [state, setState] = useState<'loading' | 'ok' | 'broken'>('loading');
  const style = size === 'lg' ? { width: 96, height: 60 } : undefined;
  if (!url)
    return (
      <div
        className="logo-thumb fallback"
        style={style}
        title="No published logo"
        role="img"
        aria-label={`${name}: no published logo`}
      >
        {initials(name)}
      </div>
    );
  return (
    <div
      className={`logo-thumb${state === 'broken' ? ' broken' : ''}`}
      style={style}
      title={state === 'broken' ? `Public object not reachable: ${url}` : url}
    >
      {state !== 'broken' && (
        <img
          src={url}
          alt={`${name} logo`}
          loading="lazy"
          onLoad={() => setState('ok')}
          onError={() => setState('broken')}
        />
      )}
      {state === 'broken' && (
        <span className="sr-only">{name}: public logo object not reachable</span>
      )}
    </div>
  );
}

// ─── States ─────────────────────────────────────────────────────────────────────────────────────────
export function Loading({ rows = 6 }: { rows?: number }) {
  return (
    <div aria-busy="true" aria-live="polite" style={{ padding: 16, display: 'grid', gap: 10 }}>
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <div key={i} className="skeleton" style={{ height: 18, width: `${90 - (i % 3) * 15}%` }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : String(error);
  const status = error instanceof ApiError ? error.status : null;
  return (
    <div className="state-box error" role="alert">
      <div>
        <h3>Could not load this data</h3>
        <p>
          {msg}
          {status ? <span className="faint"> · HTTP {status}</span> : null}
        </p>
        {onRetry && (
          <p style={{ marginTop: 12 }}>
            <button type="button" className="btn sm" onClick={onRetry}>
              Retry
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

export function Empty({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="state-box">
      <div>
        <h3>{title}</h3>
        {sub && <p>{sub}</p>}
      </div>
    </div>
  );
}

export function Notice({ tone, children }: { tone?: 'warn' | 'bad'; children: ReactNode }) {
  return (
    <div className={`notice ${tone ?? ''}`}>
      <Icon name={tone ? 'alert' : 'info'} />
      <div>{children}</div>
    </div>
  );
}

// ─── Pagination ─────────────────────────────────────────────────────────────────────────────────────
export function Pager({
  total,
  limit,
  offset,
  onChange,
  onLimit,
}: {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
  onLimit?: (limit: number) => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <nav className="pager" aria-label="Pagination">
      <span className="num">
        {fmtNum(from)}–{fmtNum(to)} of {fmtNum(total)}
      </span>
      <span className="ctl">
        {onLimit && (
          <select
            className="select"
            aria-label="Rows per page"
            value={limit}
            onChange={(e) => onLimit(Number(e.target.value))}
          >
            {[25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          className="btn sm"
          disabled={offset <= 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn sm"
          disabled={offset + limit >= total}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </button>
      </span>
    </nav>
  );
}

export const SourceTag = ({ source }: { source: string }) => (
  <span className={`src ${source}`} title={`provenance source: ${source}`}>
    {source.replace(/_/g, ' ')}
  </span>
);

export function Json({ value, summary }: { value: unknown; summary: string }) {
  return (
    <details className="raw">
      <summary>{summary}</summary>
      <pre className="json ltr" dir="ltr">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}
